/**
 * Built-in effect `circleBlur` (W2-01b; P§5.2): an "artsy" circular blur that keeps the brightest colour found
 * within `size` pixels of each pixel. Reimplemented from the behaviour (badged approximate).
 *
 * Every pixel takes the brightest (highest luma) texel it reads from the disc of radius `size` px around it, so
 * bright details grow into round blobs. The disc is read on a lattice of at most 21 x 21 texel centres:
 *
 *  - at the reference footprint (960 x 540 and below: tests, thumbnails, a 1x preview pane), up to `size` 10 (the
 *    default) the lattice is every texel of the disc: an exact max filter, so a single bright pixel grows into a
 *    solid disc;
 *  - above that, or on a larger framebuffer, the 21 x 21 lattice is spread over the disc (step `size / 10` px at
 *    the reference, scaled up by the square root of the pixel count), so it skips texels: gaps of up to
 *    `ceil(step) - 1` px between the rows and columns it reads. A detail wider than the gaps still grows into a
 *    solid disc, but a single bright pixel lights only the lattice points.
 *
 * Cost: one texture read per lattice point inside the disc, capped by the 21 x 21 lattice and scaled down with the
 * step so it does not grow with resolution. Measured (W3-04 round 2b) on an AMD Radeon 780M integrated GPU through
 * EffectBench, draw cost only (1 px readback sync), 1920 x 1080 / 3840 x 2160: a vignette baseline 0.9 / 2.3 ms;
 * circleBlur at the default size 10 was 17.7 / 71.4 ms with a texel-exact lattice at every resolution (317 reads per
 * pixel), which is over a 60 fps frame budget. The resolution-scaled step brings the default size to about 4 / 8 ms
 * on the same GPU (see W3-04.md Notes). `size` <= 0 leaves the picture unchanged.
 *
 * W3-04: the former golden-angle spiral read only about a fifth of the disc's texels, so a small bright dot came
 * out as scattered specks; a strided 17 x 17 grid that replaced it was exact only up to 8 px; the 21 x 21 lattice
 * was exact up to 10 but too expensive on integrated GPUs and high-DPR canvases.
 */

import { m } from './builtinFx.messages';
import { defineBuiltin, LUMA, SAMPLE_SCREEN } from './shared';

export const circleBlur = defineBuiltin({
  name: 'circleBlur',
  label: { key: m.circleBlur },
  description: { key: m.circleBlurDesc },
  inputs: ['screenSize'],
  uniforms: [{ name: 'size', type: 'float', default: 10, min: 0, max: 64, label: { key: m.circleBlurSize } }],
  helpers: [SAMPLE_SCREEN, LUMA],
  main: `
void main() {
  vec4 best = texture2D(screenTexture, uv);
  if (size > 0.0) {
    float bestLuma = luma(best.rgb);
    vec2 px = max(screenSize, vec2(1.0));
    vec2 centre = floor(uv * px) + 0.5;
    // Lattice step (1 = every texel) and half extent in steps (at most 10, so at most 21 x 21 reads).
    // The step grows with the square root of the pixel count past 960 x 540, so the read count (and
    // the frame cost) stays near the reference cost at 1080p, 2x DPR and 4K.
    float scale = max(1.0, sqrt(px.x * px.y / (960.0 * 540.0)));
    float stride = max(1.0, size / 10.0 * scale);
    float n = min(10.0, ceil(size / stride));
    float r2 = size * size;
    for (int j = 0; j < 21; j++) {
      float y = float(j) - n;
      if (y > n) break;
      for (int i = 0; i < 21; i++) {
        float x = float(i) - n;
        if (x > n) break;
        vec2 o = floor(vec2(x, y) * stride + 0.5);
        if (dot(o, o) > r2) continue;
        vec4 c = sampleScreen((centre + o) / px);
        float l = luma(c.rgb);
        if (l > bestLuma) {
          best = c;
          bestLuma = l;
        }
      }
    }
  }
  gl_FragColor = best;
}`,
});
